/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionIssuersUpdate } from "../funcs/organizationRemoteSessionIssuersUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RemoteSessionIssuer } from "../models/components/remotesessionissuer.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateOrganizationRemoteSessionIssuerRequest,
  UpdateOrganizationRemoteSessionIssuerSecurity,
} from "../models/operations/updateorganizationremotesessionissuer.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateOrganizationRemoteSessionIssuerMutationVariables = {
  request: UpdateOrganizationRemoteSessionIssuerRequest;
  security?: UpdateOrganizationRemoteSessionIssuerSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateOrganizationRemoteSessionIssuerMutationData =
  RemoteSessionIssuer;

export type UpdateOrganizationRemoteSessionIssuerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateIssuer organizationRemoteSessionIssuers
 *
 * @remarks
 * Update any remote_session_issuer (organizational or project-specific) in the caller's organization. Requires org:admin.
 */
export function useUpdateOrganizationRemoteSessionIssuerMutation(
  options?: MutationHookOptions<
    UpdateOrganizationRemoteSessionIssuerMutationData,
    UpdateOrganizationRemoteSessionIssuerMutationError,
    UpdateOrganizationRemoteSessionIssuerMutationVariables
  >,
): UseMutationResult<
  UpdateOrganizationRemoteSessionIssuerMutationData,
  UpdateOrganizationRemoteSessionIssuerMutationError,
  UpdateOrganizationRemoteSessionIssuerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateOrganizationRemoteSessionIssuerMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateOrganizationRemoteSessionIssuer(): MutationKey {
  return ["@gram/client", "organizationRemoteSessionIssuers", "update"];
}

export function buildUpdateOrganizationRemoteSessionIssuerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateOrganizationRemoteSessionIssuerMutationVariables,
  ) => Promise<UpdateOrganizationRemoteSessionIssuerMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateOrganizationRemoteSessionIssuer(),
    mutationFn: function updateOrganizationRemoteSessionIssuerMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateOrganizationRemoteSessionIssuerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationRemoteSessionIssuersUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
